import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react';

/** Feature flag names, mirroring `loupe_core::flags`. */
export const PRINT_ORDERING = 'print-ordering';
export const SCAN_EDITING = 'scan-editing';

export interface AppConfig {
  /**
   * Resolved feature flags for the *current* caller. An allowlisted flag reads
   * `true` only for the logged-in customers on its allowlist, so this map is
   * per-session and must not be cached across logins.
   */
  features: Record<string, boolean>;
  /** @deprecated Prefer `useFeature(PRINT_ORDERING)`. */
  printOrderingEnabled: boolean;
}

interface ConfigContextValue {
  config: AppConfig;
  loading: boolean;
}

const defaultConfig: AppConfig = {
  features: {},
  printOrderingEnabled: false,
};

const ConfigContext = createContext<ConfigContextValue | null>(null);

export function ConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<AppConfig>(defaultConfig);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchConfig() {
      try {
        const res = await fetch('/api/v1/config', { credentials: 'include' });
        if (res.ok && !cancelled) {
          const data = await res.json();
          const features: Record<string, boolean> = {};
          for (const [name, enabled] of Object.entries(data.features ?? {})) {
            features[name] = Boolean(enabled);
          }
          setConfig({
            features,
            printOrderingEnabled: Boolean(data.printOrderingEnabled),
          });
        }
      } catch {
        // On error, keep defaults (every feature disabled). Mirrors the backend
        // fail-safe: a control-plane failure hides features, never reveals them.
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchConfig();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <ConfigContext.Provider value={{ config, loading }}>
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig(): ConfigContextValue {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
}

/**
 * Is `flag` enabled for the current caller?
 *
 * Returns `false` while config is still loading, so gated UI never flashes into
 * view before the flag resolves.
 *
 * This is a rendering hint only. The server independently enforces every flag at
 * its own endpoint — hiding a button is not a security boundary.
 */
export function useFeature(flag: string): boolean {
  const { config, loading } = useConfig();
  return !loading && config.features[flag] === true;
}
